import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Package, ArrowRight, Clock, Store } from 'lucide-react';
import { Button } from '../components/design-system';
import { trackPurchase } from '../utils/fbPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, pending_oxxo, error
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const purchaseTracked = useRef(false); // Prevent duplicate tracking

  const checkPaymentStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Error al verificar el pago');
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        
        // Track Purchase for Facebook Pixel (only once)
        if (!purchaseTracked.current) {
          purchaseTracked.current = true;
          trackPurchase({
            items: data.items || [],
            totals: { total: data.amount || 0 },
            orderId: data.order_id,
            orderNumber: data.order_id?.substring(0, 8).toUpperCase(),
            payment_method: data.payment_method || 'stripe'
          });
        }
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('La sesión de pago ha expirado');
      } else if (data.payment_method === 'oxxo' || data.payment_status === 'unpaid') {
        // OXXO payment - show pending voucher status
        setStatus('pending_oxxo');
      } else if (pollCount < 5) {
        // Payment still processing, check again in 2 seconds (max 5 times)
        setPollCount(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      } else {
        // After 5 polls, assume it's an async payment method like OXXO
        setStatus('pending_oxxo');
      }
    } catch (err) {
      console.error('Payment status error:', err);
      setStatus('error');
      setError(err.message || 'Error al verificar el estado del pago');
    }
  }, [sessionId, pollCount]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
      setError('No se encontró la sesión de pago');
    }
  }, [sessionId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  if (status === 'loading') {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="checkout-success-loading">
        <Loader2 className="w-16 h-16 mx-auto mb-6 text-brand-cobalt animate-spin" />
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
          Verificando tu pago...
        </h1>
        <p className="text-brand-black/50">
          Por favor espera mientras confirmamos tu transacción.
        </p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="checkout-success-error">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
          Error en el pago
        </h1>
        <p className="text-brand-black/50 mb-8">
          {error || 'Hubo un problema al procesar tu pago. Por favor intenta nuevamente.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button variant="primary" onClick={() => navigate('/cart')}>
            Volver al carrito
          </Button>
          <Button variant="ghost" onClick={() => navigate('/contact')}>
            Contactar soporte
          </Button>
        </div>
      </div>
    );
  }

  // OXXO Pending Payment View
  if (status === 'pending_oxxo') {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto" data-testid="checkout-success-oxxo">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vg1ejfh4_logo-alphatica.png"
            alt="Alphatica"
            className="h-14 w-auto mx-auto"
          />
        </div>

        {/* Pending Icon */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Store className="w-12 h-12 text-amber-600" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-2">
            ¡Casi listo!
          </h1>
          <p className="text-brand-black/50 text-lg">
            Tu voucher de OXXO ha sido generado
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-brand-bone p-8 mb-8">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-brand-black/15">
            <Package className="w-6 h-6 text-brand-cobalt" />
            <h2 className="font-display text-xl">Detalles del pedido</h2>
          </div>

          <div className="space-y-4">
            {paymentData?.order_id && (
              <div className="flex justify-between items-center">
                <span className="text-brand-black/60">Número de orden</span>
                <span className="font-mono font-medium text-brand-black">
                  {paymentData.order_id.substring(0, 8).toUpperCase()}
                </span>
              </div>
            )}
            
            {paymentData?.amount && (
              <div className="flex justify-between items-center">
                <span className="text-brand-black/60">Total a pagar</span>
                <span className="font-display text-xl text-brand-black">
                  {formatPrice(paymentData.amount)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-brand-black/60">Método de pago</span>
              <span className="inline-flex items-center gap-2 font-medium">
                <Store size={16} className="text-amber-600" />
                OXXO
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-brand-black/60">Estado</span>
              <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                <Clock size={16} />
                Pendiente de pago
              </span>
            </div>
          </div>
        </div>

        {/* OXXO Instructions */}
        <div className="bg-amber-50 border border-amber-200 p-6 mb-8">
          <h3 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
            <Store size={20} />
            Instrucciones para pagar en OXXO
          </h3>
          <ol className="space-y-3 text-sm text-amber-700">
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600">1.</span>
              Revisa tu correo electrónico donde recibirás el voucher de pago con el código de barras.
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600">2.</span>
              Acude a cualquier tienda OXXO y muestra el voucher al cajero.
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600">3.</span>
              Realiza el pago en efectivo por el monto exacto indicado.
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600">4.</span>
              Guarda tu comprobante. Tu pedido será procesado automáticamente al confirmar el pago.
            </li>
          </ol>
          <div className="mt-4 pt-4 border-t border-amber-200">
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <Clock size={14} />
              <strong>Importante:</strong> El voucher expira en 3 días. Si no realizas el pago, tu pedido será cancelado.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            variant="primary" 
            onClick={() => navigate('/products')}
            className="flex items-center gap-2"
          >
            Seguir comprando
            <ArrowRight size={18} />
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
          >
            Volver al inicio
          </Button>
        </div>

        {/* Support Note */}
        <p className="text-center text-sm text-brand-black/50 mt-8">
          ¿Tienes alguna pregunta?{' '}
          <a href="/contact" className="text-brand-cobalt hover:underline">
            Contáctanos
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto" data-testid="checkout-success-page">
      {/* Logo */}
      <div className="text-center mb-8">
        <img 
          src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vg1ejfh4_logo-alphatica.png"
          alt="Alphatica"
          className="h-14 w-auto mx-auto"
        />
      </div>

      {/* Success Icon */}
      <div className="text-center mb-8">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-2">
          ¡Pago exitoso!
        </h1>
        <p className="text-brand-black/50 text-lg">
          Tu pedido ha sido confirmado y está siendo procesado.
        </p>
      </div>

      {/* Order Details Card */}
      <div className="bg-brand-bone p-8 mb-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-brand-black/15">
          <Package className="w-6 h-6 text-brand-cobalt" />
          <h2 className="font-display text-xl">Detalles del pedido</h2>
        </div>

        <div className="space-y-4">
          {paymentData?.order_id && (
            <div className="flex justify-between items-center">
              <span className="text-brand-black/60">Número de orden</span>
              <span className="font-mono font-medium text-brand-black">
                {paymentData.order_id.substring(0, 8).toUpperCase()}
              </span>
            </div>
          )}
          
          {paymentData?.amount && (
            <div className="flex justify-between items-center">
              <span className="text-brand-black/60">Total pagado</span>
              <span className="font-display text-xl text-brand-black">
                {formatPrice(paymentData.amount)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-brand-black/60">Estado del pago</span>
            <span className="inline-flex items-center gap-1 text-green-600 font-medium">
              <CheckCircle size={16} />
              Pagado
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-brand-black/60">Estado del pedido</span>
            <span className="text-amber-600 font-medium">
              En preparación
            </span>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-blue-50 border border-blue-200 p-6 mb-8">
        <h3 className="font-medium text-blue-800 mb-3">¿Qué sigue?</h3>
        <ul className="space-y-2 text-sm text-blue-700">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">1.</span>
            Recibirás un mensaje de WhatsApp con la confirmación de tu pedido.
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">2.</span>
            Tu pedido será preparado y enviado en 1-2 días hábiles.
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">3.</span>
            Te enviaremos el número de guía para rastrear tu paquete.
          </li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button 
          variant="primary" 
          onClick={() => navigate('/products')}
          className="flex items-center gap-2"
        >
          Seguir comprando
          <ArrowRight size={18} />
        </Button>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
        >
          Volver al inicio
        </Button>
      </div>

      {/* Support Note */}
      <p className="text-center text-sm text-brand-black/50 mt-8">
        ¿Tienes alguna pregunta?{' '}
        <a href="/contact" className="text-brand-cobalt hover:underline">
          Contáctanos
        </a>
      </p>
    </div>
  );
};

export default CheckoutSuccess;
